import React, { useEffect, useMemo, useState } from 'react'
import pluralize from 'pluralize'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Modal } from '../../components/Modal/Modal'
import { type InviteState, useInviteUserMutation, useLazyGetInvitesQuery } from '../../store/auth/auth'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { CustomerFreeSeatsResponse, useGetBillingCustomerFreeSeatsQuery } from '../../store/plan/plan'
import { Button } from '../../components/Button/Button'
import { SelectDropdown } from '../../components/SelectDropdown/SelectDropdown'
import { getInviteRoleGroups } from './InviteRoleGroups'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { getUserName } from '../utils'
import { AvailableSeatsInfo } from './AvailableSeatsInfo'
import { LinkButton } from '../../components/Button/LinkButton'
import { useDebouncedCallback } from 'use-debounce'
import { DEFAULT_PAGE_SIZE } from '../../common/constants'
import { defaultPendingInvitationsFilter } from './PendingInvitations/utils'
import { addToast } from '../../store/toast/toastSlice'
import { isApiErrors } from '../../models/ApiErrors'
import { type RoleType } from '../../models/Role'
import { BuySeatDialog } from './BuySeatModal'
import { getInviteSeatState, type InviteSeatState } from './inviteSeatState'
import { seatTypeLabels } from './roleChangeSeatImpact'
import { SeatInfoBox } from './SeatInfoBox'
import { useInviteFlowRevampEnabled } from './useInviteFlowRevampEnabled'
import { useRefreshSeats } from './useRefreshSeats'
import { useSeatPurchaseGate } from './seatPurchaseGate'

export interface InviteUserModalProps {
  onHide: () => void
  onInvited?: (state: InviteState) => void
}

interface InvitePurchaseTarget {
  invitationId: string
  seatType: RoleType
  email: string
}

type FormValues = {
  firstName: string
  lastName: string
  email: string
  roleGroupName: string
}

const readInviteError = (error: unknown) => {
  const body = typeof error === 'object' && error !== null && 'data' in error ? error.data : null
  if (isApiErrors(body) && Array.isArray(body.errors) && body.errors.length > 0) {
    return body.errors[0].message
  }
  return 'The invitation could not be sent. Please try again.'
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({ onHide, onInvited }) => {
  const { tracked, trackingClient } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const inviteRoleGroups = useMemo(() => getInviteRoleGroups({ onlyInvitable: true }), [])
  const [hasValidationErrors, setValidationErrors] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const {
    data: freeSeatsData,
    isFetching: isFreeSeatsFetching,
    isError: isFreeSeatsFetchingError,
    refetch: refetchFreeSeats,
  } = useGetBillingCustomerFreeSeatsQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const { isEnabled: revampEnabled, isLoading: revampLoading } = useInviteFlowRevampEnabled()
  const canBuySeats = useSeatPurchaseGate()
  const refreshSeats = useRefreshSeats()
  const [purchaseTarget, setPurchaseTarget] = useState<InvitePurchaseTarget | null>(null)

  const defaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    roleGroupName: getInviteRoleGroups({ onlyInvitable: true })[0].name,
  }

  const [inviteUser, { isLoading: isInviteLoading, isSuccess }] = useInviteUserMutation()

  const buildRequest = (data: FormValues) => ({
    accountId,
    userData: {
      firstName: data.firstName,
      lastName: data.lastName,
      name: getUserName(data.firstName, data.lastName) || undefined,
      email: data.email,
      roleIds: inviteRoleGroups.find((group) => group.name === data.roleGroupName)?.roleIds || [],
    },
  })

  const handleSave = tracked(
    (data: FormValues) => {
      inviteUser(buildRequest(data))
    },
    { eventName: TrackEvent.INVITE_USER_SENT },
    {
      eventDetailsExtractor: (data: FormValues) => {
        return {
          email: data.email,
        }
      },
    },
  )

  const form = useForm<FormValues>({ defaultValues })
  const { handleSubmit, reset, control } = form
  const selectedRoleGroupName = useWatch({ control, name: 'roleGroupName' })

  const seatState = getInviteSeatState({
    seats: isFreeSeatsFetchingError ? undefined : freeSeatsData?.data.seats,
    seatType: inviteRoleGroups.find((group) => group.name === selectedRoleGroupName)?.type,
    canBuy: canBuySeats,
  })

  const handleRevampSave = async (data: FormValues) => {
    setInviteError(null)
    try {
      const { state, invitationId } = await inviteUser(buildRequest(data)).unwrap()
      refreshSeats()
      const isRequested = state === 'requested'
      trackingClient.track({
        eventName: isRequested ? TrackEvent.INVITE_REQUESTED : TrackEvent.INVITE_USER_SENT,
        eventDetails: { email: data.email },
      })
      if (isRequested && seatState.kind === 'shortage' && seatState.canBuy) {
        setPurchaseTarget({ invitationId, seatType: seatState.seatType, email: data.email })
        return
      }
      dispatch(addToast({ variant: 'success', title: isRequested ? 'Seat was requested for this invite' : 'Invitation sent' }))
      reset()
      onInvited?.(state)
      onHide()
    } catch (error) {
      setInviteError(readInviteError(error))
      refetchFreeSeats()
    }
  }

  const closeAsRequested = () => {
    dispatch(addToast({ variant: 'success', title: 'Seat was requested for this invite' }))
    reset()
    onInvited?.('requested')
    onHide()
  }

  useEffect(() => {
    if (isSuccess && !revampEnabled) {
      onHide()
      reset()
    }
  }, [isSuccess, revampEnabled, onHide, reset])

  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Invite User</h4>
        </Col>
      </Row>

      <Row>
        <FormProvider {...form}>
          <InviteUserForm
            freeSeatsData={freeSeatsData?.data}
            isFreeSeatsFetching={isFreeSeatsFetching}
            isFreeSeatsFetchingError={isFreeSeatsFetchingError}
            setValidationErrors={setValidationErrors}
            revampEnabled={revampEnabled}
            seatState={seatState}
            inviteError={inviteError}
          />
        </FormProvider>
      </Row>
    </Container>
  )

  const hasAvailableSeat = () => {
    return !isFreeSeatsFetching
      ? selectedRoleGroupName === 'Administrative Assistant' || selectedRoleGroupName === 'Administrative Assistant with Templates'
        ? freeSeatsData?.data.seats.sidecar.available !== 0
        : freeSeatsData?.data.seats.general.available !== 0
      : false
  }

  const legacyFooter = (
    <div className='d-flex gap-3'>
      <Button variant='secondary' onClick={onHide}>
        Cancel
      </Button>
      <ScopedBlock scope={{ scope: 'consoleuser:invite', superScope: ['consoleuser:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
        <ScopedBlock
          tooltipText='Buy more seats to add users'
          tooltipButtonText='Buy more seats'
          authActions={[hasAvailableSeat]}
          missingScopeAction={MissingScopeAction.UPGRADE}
        >
          <Button
            variant='primary'
            onClick={handleSubmit(handleSave)}
            loading={isInviteLoading}
            title={!hasAvailableSeat() ? 'You need to buy more seats to invite more users' : ''}
            disabled={hasValidationErrors}
          >
            Invite
          </Button>
        </ScopedBlock>
      </ScopedBlock>
    </div>
  )

  const revampFooter = (
    <div className='d-flex gap-3'>
      <Button variant='secondary' onClick={onHide}>
        Cancel
      </Button>
      <ScopedBlock scope={{ scope: 'consoleuser:invite', superScope: ['consoleuser:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
        <Button variant='primary' onClick={handleSubmit(handleRevampSave)} loading={isInviteLoading} disabled={hasValidationErrors}>
          {seatState.kind === 'shortage' ? 'Add' : 'Send invite'}
        </Button>
      </ScopedBlock>
    </div>
  )

  if (revampLoading) {
    return (
      <Modal
        show
        onHide={onHide}
        header={<></>}
        body={
          <div className='d-flex align-items-center justify-content-center p-4'>
            <Spinner />
          </div>
        }
        showFooterActions={false}
        centered={true}
      />
    )
  }

  if (purchaseTarget) {
    return (
      <BuySeatDialog
        invitationId={purchaseTarget.invitationId}
        seatType={purchaseTarget.seatType}
        email={purchaseTarget.email}
        firstLine={`You're about to buy 1 ${seatTypeLabels[purchaseTarget.seatType]} seat.`}
        secondLine="Once you confirm, we'll send their invitation. Not now? The invitation goes to the requested list instead."
        centered
        onDismiss={closeAsRequested}
        onSeatsChanged={refreshSeats}
        onSent={() => {
          reset()
          onInvited?.('pending')
          onHide()
        }}
        onGone={onHide}
      />
    )
  }

  return <Modal show onHide={onHide} header={<></>} body={body} footer={revampEnabled ? revampFooter : legacyFooter} centered={true} />
}

interface InviteSeatNoteProps {
  seatState: InviteSeatState
  isFetching: boolean
}

const InviteSeatNote = ({ seatState, isFetching }: InviteSeatNoteProps) => {
  if (isFetching) {
    return (
      <SeatInfoBox>
        <Spinner size='sm' />
      </SeatInfoBox>
    )
  }

  if (seatState.kind === 'available') {
    return (
      <SeatInfoBox title={`${seatState.remaining} ${pluralize('seat', seatState.remaining)} left after this invitation`}>
        We'll send an invitation email. The seat is held until they accept.
      </SeatInfoBox>
    )
  }

  if (seatState.kind === 'shortage') {
    return (
      <SeatInfoBox title={`No ${seatTypeLabels[seatState.seatType]} seats available`}>
        {seatState.canBuy
          ? 'Add the invitation to the requested list. Buy seat and it goes out automatically.'
          : 'The invitation will be added to the requested list.'}
      </SeatInfoBox>
    )
  }

  return <SeatInfoBox>Unable to fetch free seats information</SeatInfoBox>
}

interface InviteUserFormProps {
  freeSeatsData?: CustomerFreeSeatsResponse
  isFreeSeatsFetching: boolean
  isFreeSeatsFetchingError: boolean
  setValidationErrors: (hasErrors: boolean) => void
  revampEnabled: boolean
  seatState: InviteSeatState
  inviteError: string | null
}

const InviteUserForm = ({
  freeSeatsData,
  isFreeSeatsFetching,
  isFreeSeatsFetchingError,
  setValidationErrors,
  revampEnabled,
  seatState,
  inviteError,
}: InviteUserFormProps) => {
  const { control } = useFormContext<FormValues>()
  const accountId = useAppSelector(selectAccountId)
  const inviteRoleGroups = useMemo(() => getInviteRoleGroups({ onlyInvitable: true }), [])
  const [triggerGetFilteredInvites, query] = useLazyGetInvitesQuery()

  const invitationStateOfEmail = query.data && Array.isArray(query.data.data) && query.data.data.length > 0 ? query.data.data[0].state : null

  const handleEmailChanged = useDebouncedCallback((email: string) => {
    triggerGetFilteredInvites({
      accountId,
      filter: {
        ...defaultPendingInvitationsFilter,
        emailPattern: email,
        ...(revampEnabled ? { types: ['invite_code' as const, 'invite_request' as const] } : {}),
      },
      ...(revampEnabled ? { includeExpired: false } : {}),
      pageRequest: {
        page: 0,
        size: DEFAULT_PAGE_SIZE,
      },
    })
  }, 500)

  useEffect(() => {
    setValidationErrors(invitationStateOfEmail !== null)
  }, [invitationStateOfEmail, setValidationErrors])

  const hasNoAvailableSeat = freeSeatsData ? freeSeatsData.seats.general.available === 0 && freeSeatsData.seats.sidecar.available === 0 : true
  const duplicateMessage =
    invitationStateOfEmail === 'requested' ? 'A seat request already exists for this email' : `${invitationStateOfEmail} invitation already exists`

  return (
    <>
      <Container className='d-flex flex-column gap-4 p-0'>
        {revampEnabled ? null : (
          <Row>
            <Col>
              <div>
                <div>
                  <AvailableSeatsInfo seats={freeSeatsData?.seats} isFetching={isFreeSeatsFetching} isError={isFreeSeatsFetchingError} />
                </div>
                <div className='d-inline-flex pt-2'>
                  <LinkButton variant='primary' to='/account/plan'>
                    Buy more
                  </LinkButton>
                </div>
              </div>
            </Col>
          </Row>
        )}
        {revampEnabled || !hasNoAvailableSeat ? (
          <>
            <Row>
              <Col>
                <Controller
                  name='email'
                  control={control}
                  rules={{
                    required: 'Email address is required.',
                    pattern: {
                      value: /\S+@\S+\.\S+/,
                      message: 'Entered value does not match email format',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <>
                      <Form.Label>Email address*</Form.Label>
                      <Form.Control
                        {...field}
                        id={field.name}
                        type='email'
                        placeholder='Enter email address…'
                        onChange={(event) => {
                          field.onChange(event)
                          handleEmailChanged(event.currentTarget.value)
                        }}
                        isInvalid={!!fieldState.error || invitationStateOfEmail !== null}
                      />
                      {fieldState.error || invitationStateOfEmail ? (
                        <FormFeedback>{fieldState.error?.message ?? (invitationStateOfEmail ? duplicateMessage : '')}</FormFeedback>
                      ) : null}
                      {inviteError ? <FormFeedback className='mt-2'>{inviteError}</FormFeedback> : null}
                    </>
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Controller
                  name='firstName'
                  control={control}
                  render={({ field }) => (
                    <>
                      <Form.Label>First name</Form.Label>
                      <Form.Control id={field.name} type='text' placeholder='Enter first name…' {...field} />
                    </>
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Controller
                  name='lastName'
                  control={control}
                  render={({ field }) => (
                    <>
                      <Form.Label>Last name</Form.Label>
                      <Form.Control id={field.name} type='text' placeholder='Enter last name…' {...field} />
                    </>
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Controller
                  name='roleGroupName'
                  control={control}
                  render={({ field, fieldState }) => (
                    <>
                      <Form.Label>Role</Form.Label>
                      <SelectDropdown
                        className='zocks-px-0.75 py-2'
                        options={inviteRoleGroups.map((roleGroup) => {
                          return { id: roleGroup.name, label: roleGroup.name }
                        })}
                        initialSelectedOption={inviteRoleGroups
                          .map((roleGroup) => {
                            return { id: roleGroup.name, label: roleGroup.name }
                          })
                          .find((option) => option.id === field.value)}
                        onOptionSelected={(option) => field.onChange(option.id)}
                      />
                      {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                      {revampEnabled ? <InviteSeatNote seatState={seatState} isFetching={isFreeSeatsFetching} /> : null}
                    </>
                  )}
                />
              </Col>
            </Row>
          </>
        ) : null}
      </Container>
    </>
  )
}
